import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";
import { StoreInfo, Address } from "../types";
import GoogleMap from "../components/GoogleMap";

/**
 * Formats an address object into JSX, only displaying fields that have values.
 * Handles missing or empty address fields gracefully.
 */
const formatAddress = (address: Address): React.ReactNode => {
  const parts: string[] = [];

  // Street address
  if (address.street && address.street.trim()) {
    parts.push(address.street.trim());
  }

  // City, State, ZipCode line
  const cityStateZip: string[] = [];
  if (address.city && address.city.trim()) {
    cityStateZip.push(address.city.trim());
  }
  if (address.state && address.state.trim()) {
    cityStateZip.push(address.state.trim());
  }
  if (address.zipCode && address.zipCode.trim()) {
    cityStateZip.push(address.zipCode.trim());
  }
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  // Country
  if (address.country && address.country.trim()) {
    parts.push(address.country.trim());
  }

  // If no address parts available, return a message
  if (parts.length === 0) {
    return <span className="text-gray-400 italic">Address not available</span>;
  }

  // Return formatted address with line breaks
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

interface ContactPageProps {
  storeInfo: StoreInfo;
}

export const ContactPage: React.FC<ContactPageProps> = ({ storeInfo }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle pre-filled subject from navigation state (e.g., from "Suggest a Toy" button)
  useEffect(() => {
    if (location.state?.prefillSubject) {
      setFormData((prev) => ({
        ...prev,
        subject: location.state.prefillSubject,
      }));
    }
  }, [location.state]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create email content
      const emailSubject = `Contact Form: ${formData.subject}`;
      const emailBody = `Hello Benipal Toys Team,

I would like to get in touch regarding: ${formData.subject}

My Details:
- Name: ${formData.firstName} ${formData.lastName}

Message:
${formData.message}

Looking forward to hearing from you!

Best regards,
${formData.firstName} ${formData.lastName}`;

      // Create mailto link
      const mailtoLink = `mailto:${
        storeInfo.email
      }?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(
        emailBody
      )}`;

      // Open email app
      window.location.href = mailtoLink;

      // Show success message
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after a delay
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          subject: "",
          message: "",
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error("Error opening email app:", error);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-cream-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-soft-blue-100 via-soft-purple-50 to-soft-pink-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-4">📞</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            We'd love to hear from you! Get in touch with us for any questions
            or inquiries.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Get in Touch
              </h2>

              {/* Store Info Cards */}
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Address
                    </h3>
                    <p className="text-gray-600">
                      {formatAddress(storeInfo.address)}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <div className="space-y-1">
                      {storeInfo.phone && storeInfo.phone.trim() && (
                        <a
                          href={`tel:${storeInfo.phone}`}
                          className="block text-green-600 hover:text-green-700 transition-colors duration-200"
                        >
                          {storeInfo.phone}
                        </a>
                      )}
                      {storeInfo.phone2 && storeInfo.phone2.trim() && (
                        <a
                          href={`tel:${storeInfo.phone2}`}
                          className="block text-green-600 hover:text-green-700 transition-colors duration-200"
                        >
                          {storeInfo.phone2}
                        </a>
                      )}
                      {(!storeInfo.phone || !storeInfo.phone.trim()) &&
                        (!storeInfo.phone2 || !storeInfo.phone2.trim()) && (
                          <span className="text-gray-400 italic">
                            Phone not available
                          </span>
                        )}
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    {storeInfo.email && storeInfo.email.trim() ? (
                      <a
                        href={`mailto:${storeInfo.email}`}
                        className="text-blue-600 hover:text-blue-700 transition-colors duration-200"
                      >
                        {storeInfo.email}
                      </a>
                    ) : (
                      <span className="text-gray-400 italic">
                        Email not available
                      </span>
                    )}
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Store Hours
                    </h3>
                    {storeInfo.hours.weekdays &&
                    storeInfo.hours.weekdays.trim() ? (
                      <p className="text-gray-600">
                        <span className="text-xs"> Mon - Fri: </span>
                        {storeInfo.hours.weekdays}
                      </p>
                    ) : (
                      <p className="text-gray-400 italic">
                        Hours not available
                      </p>
                    )}
                    {storeInfo.hours.weekends &&
                      storeInfo.hours.weekends.trim() && (
                        <p className="text-gray-600 mt-1">
                          <span className="text-xs"> Sat - Sun: </span>
                          {storeInfo.hours.weekends}
                        </p>
                      )}
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Follow Us
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a
                  href={storeInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-4 bg-pink-50 hover:bg-pink-100 rounded-lg transition-colors duration-200"
                >
                  <div className="text-pink-600 text-xl">📷</div>
                  <span className="font-medium text-gray-900">Instagram</span>
                </a>
                <a
                  href={storeInfo.socialMedia.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-4 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
                >
                  <div className="text-red-600 text-xl">📺</div>
                  <span className="font-medium text-gray-900">YouTube</span>
                </a>
                <a
                  href={storeInfo.socialMedia.whatsappChannel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200"
                >
                  <div className="text-green-600 text-xl">💬</div>
                  <span className="font-medium text-gray-900">
                    WhatsApp Updates
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Send us a Message
            </h2>

            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Message Sent!
                </h3>
                <p className="text-gray-600 mb-4">
                  Your email app should open with a pre-filled message. Please
                  send the email to complete your inquiry!
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your last name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="product">Product Question</option>
                    <option value="order">Order Support</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <span className="text-xl mr-2 animate-bounce">📧</span>
                      Opening Email...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Find Us
          </h2>
          <GoogleMap />
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
